import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';

const separatorVariants = cva('shrink-0 opacity-20', {
  variants: {
    orientation: {
      horizontal: 'h-px w-full',
      vertical: 'h-full w-px',
    },
    color: {
      base: 'bg-background-inverted',
      inverted: 'bg-background',
    },
  },
  defaultVariants: { orientation: 'horizontal', color: 'base' },
});

export type SeparatorProps = ComponentProps<'div'> &
  VariantProps<typeof separatorVariants> & {
    /**
     * Purely visual when true (default): the line is removed from the
     * accessibility tree (`role="none"`). Set false to expose it as a semantic
     * `role="separator"` divider between content groups.
     */
    decorative?: boolean;
  };

export function Separator({
  className,
  orientation = 'horizontal',
  color = 'base',
  decorative = true,
  ...props
}: SeparatorProps) {
  return (
    <div
      data-slot="separator"
      data-orientation={orientation}
      role={decorative ? 'none' : 'separator'}
      // `aria-orientation` is implicit `horizontal` for a separator, so only the
      // vertical, non-decorative case needs to declare it.
      aria-orientation={!decorative && orientation === 'vertical' ? 'vertical' : undefined}
      className={clsxm(separatorVariants({ orientation, color }), className)}
      {...props}
    />
  );
}

export { separatorVariants };
